import { firstValueFrom, from, lastValueFrom, Observable, of, Subject, zip } from 'rxjs';
import { concatMap, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FormatDate } from '../../_objects/format-date';
import { User, UserTenantAttribute } from '../../_objects/user';
import { CommonSnackBarService } from '../../_services/common-snack-bar.service';
import { AppInfoDbService } from '../../_services/database/app-info-db.service';
import { CommonDbService } from '../../_services/database/common-db.service';
import { LoggedInUserDbService } from '../../_services/database/logged-in-user-db.service';
import { FileSystemAccessService } from '../../_services/file-system-access.service';
import { LoginUserOnlineService } from '../../_services/http-api/login-user-online.service';
import { LangUpdateService } from '../../_services/lang-update.service';
import { NetworkStatusService } from '../../_services/network-status.service';
import { LoginUserOfflineService } from '../../_services/offline/login-user-offline.service';
import { SpinnerService } from '../../_services/spinner.service';
import { APP_LAUNCH_TYPE, IPC_CHANNEL, REGION_CONFIG, STANDALONE } from '../electron.constants';
import { AppInfoSchema } from '../models/app-info.schema';
import { LoggedInUserSchema } from '../models/logged-in-user.schema';
import { OfflineCookieService } from './offline-cookie.service';
import { OfflineUtilsService } from './offline-utils.service';
import { StandaloneDataService } from './standalone-data.service';
import { IplexGConnectModeService } from './iplex-g-connect-mode.service';
import { IplexGSyncService } from './iplex-g-sync.service';
import { CommonTerminateConfirmDialogComponent } from '../../common-dialog/common-terminate-confirm-dialog/common-terminate-confirm-dialog.component';
import { ElectronService } from '../../electron.service';
import { IndividualReportTemplateDbService } from '../../_services/database/individual-report-template-db.service';
import { ReportTemplateOfflineService } from '../../_services/offline/report-template-offline.service';
import { IndividualReportTemplateSchema } from '../models/individual-report-template.schema';
import { AuthService } from "@evc/platform";
import { TenantUserService } from '../../_services/http-api/tenant-user.service';
import { net } from 'electron';

@Injectable({
  providedIn: 'root'
})
export class OfflineSetupService {
  private _isUserSelected = false;
  private adminRoles = ["Owner", "Admin"];

  get isUserSelected() {
    return this._isUserSelected;
  }

  constructor(
    private dialog: MatDialog,
    private electronService: ElectronService,
    private appInfoDbService: AppInfoDbService,
    private loggedInUserDbService: LoggedInUserDbService,
    private networkStatusService: NetworkStatusService,

    private cookieService: OfflineCookieService,
    private authService: AuthService,
    private loginUserOnlineService: LoginUserOnlineService,
    private loginUserOfflineService: LoginUserOfflineService,
    private translate: TranslateService,
    private oscSpinnerService: SpinnerService,
    private fileSystemAccessService: FileSystemAccessService,
    private commonDbService: CommonDbService,
    private offlineUtilsService: OfflineUtilsService,
    private standaloneDataService: StandaloneDataService,
    private iplexGConnectModeService: IplexGConnectModeService,
    private iplexGSyncService: IplexGSyncService,
  ) {
  }

  async setupDesktopApp(routeOf: Observable<Params>, onAppDestroy: Observable<any>) {

    let appInfo: AppInfoSchema;
    let loggedInUserList: LoggedInUserSchema[];

    const [initAppInfo, initLoggedInUserList] = await this.getInitData();
    appInfo = initAppInfo;
    loggedInUserList = initLoggedInUserList;

    if (!APP_LAUNCH_TYPE.VALUES.includes(appInfo?.appLaunchType)) {
      appInfo = await this.setAppLaunchType();
    }

    await this.setRegion(appInfo);

    if (loggedInUserList.length === 0) {
      loggedInUserList = await this.setIPLEXModeUser();
      await this.setUser(appInfo, loggedInUserList);
    }

    this.setSubscribes(routeOf, onAppDestroy);
  }

  switchNetworkMode(isOnline: boolean) {
    if (isOnline) {
      this.networkStatusService.switchOnlineMode();
    } else {
      this.networkStatusService.switchOfflineMode();
    }
  }
  async approveUserOffline(me: User) {
    this.loginUserOfflineService.loginUser = me;
    const user = this.loginUserOfflineService.loginUser;

    if (!user.tenantAttributes?.length)
      user.tenantAttributes = this.getBasicTenantAttributes();

    await this.loginUserOfflineService.setTenantAndUserProfile();
    if (this.networkStatusService.isOnline) {
      this.loginUserOnlineService.loginUser = me;
      await this.loginUserOnlineService.setTenantAndUserProfile();
    }

    // add logged in user table
    const tenantId = this.loginUserOfflineService.tenantId;
    const date = new FormatDate();
    const loggedInUserSchema = new LoggedInUserSchema(me, this.networkStatusService.isOnline, date.toString(), tenantId);
    await firstValueFrom(this.loggedInUserDbService.put(loggedInUserSchema));
  }

  getBasicTenantAttributes(): UserTenantAttribute[] {
    const claims = this.authService.idTokenClaims();
    if (!claims) {
      return [];
    }

    const userTenantAttribute = new UserTenantAttribute();
    userTenantAttribute.role = claims.platformRoles as string;
    userTenantAttribute.id = this.authService.organizationId();

    return [userTenantAttribute];
  }

  resetAppData() {
    return from(this.deleteAppData()).pipe(mergeMap(() => this.setIPLEXModeUser()));
  }

  private async getInitData() {
    return await zip(
      this.appInfoDbService.get(),
      this.loggedInUserDbService.getAll(),
    ).toPromise();
  }

  private async setRegion(appInfo: AppInfoSchema) {
    // set region
    if (appInfo?.region && REGION_CONFIG.VALUES.some(v => v.country === appInfo.region)) {
      const regionInfo = REGION_CONFIG.VALUES.find(v => v.country === appInfo.region);
      return await this.loadEnvConfig(regionInfo);
    } else {
      this.loadEnvConfig(REGION_CONFIG.VALUES[0]);
    }
  }

  private async setUser(appInfo: AppInfoSchema, loggedInUserList: LoggedInUserSchema[]) {
    if (!appInfo?.isAfterStartup) {
      await firstValueFrom(this.appInfoDbService.isAfterStartUp());
      // use default user or last logged in available user
      const onlineUser = loggedInUserList
        .find(u => u.id != STANDALONE.USER_ID);
      const currentUser = onlineUser ?? loggedInUserList[0];
      return await this.updateLoggedInUserOnOffline(currentUser);
    }

    return [null, appInfo];
  }

  private setSubscribes(routeOf: Observable<Params>, onDestroy: Observable<any>) {

    // Subscribe to app termination events from Electron
    this.electronService.onEvent(IPC_CHANNEL.MAIN_PROCESS.MAIN_WINDOW_CLOSE, (event, arg) => {
      // In IPLEX G connection mode, call the disconnection API and then close the app.
      if (this.iplexGConnectModeService.isHttpsConnectMode) {
        this.iplexGSyncService.stopHttpsP2P()
          .pipe(take(1))
          .subscribe(_ => _);
      }

      this.offlineUtilsService.exitApp();
    });
  }

  private async deleteAppData() {
    this.oscSpinnerService.show(true);
    this.networkStatusService.setIsOnPremise(true);
    return await Promise.all([
      this.cookieService.deleteAllCookies(),
      this.fileSystemAccessService.deleteFolder(''),
      this.commonDbService.deleteAll()
    ])
      .then(_ => this.offlineUtilsService.loadAppUrl())
      .then(() => {

        this.oscSpinnerService.hide(true);
      });
  }

  private openTerminateAppConfirmDialog() {
    return this.dialog.open(CommonTerminateConfirmDialogComponent, {
      minWidth: '450px',
      maxHeight: '485px',
      minHeight: '150px',
      data: {
        firstMessage: this.translate.instant('commonTerminateConfirmDialog.initializeAppDataMessage1'),
        secondMessage: this.translate.instant('commonTerminateConfirmDialog.initializeAppDataMessage2'),
        thirdMessage: this.translate.instant('commonTerminateConfirmDialog.initializeAppDataMessage3'),
        trueBtnText: this.translate.instant('commonConfirmDialog.yes'),
        falseBtnText: this.translate.instant('commonConfirmDialog.no'),
        dialogType: 'confirmation',
        isWarnButton: true
      }
    });
  }

  private async loadEnvConfig(env: REGION_CONFIG.RegionInfo) {
    return await lastValueFrom(this.appInfoDbService.holdEnvironment(env.country));
  }

  private async updateLoggedInUserOnOffline(user: LoggedInUserSchema) {
    let userPromise: Promise<LoggedInUserSchema>;
    if (!user.currentNetworkMode) {
      // Offline mode users will update their login time
      user.lastLoggedInDate = new FormatDate().toString();
      userPromise = this.loggedInUserDbService.put(user).toPromise();
    } else {
      userPromise = of(user).toPromise();
    }

    return await Promise.all([
      userPromise,
      this.appInfoDbService.putNetworkMode(user.currentNetworkMode).toPromise()
    ]);
  }

  private async setAppLaunchType(): Promise<AppInfoSchema> {
    return await firstValueFrom(this.appInfoDbService.holdAppLaunchType(APP_LAUNCH_TYPE.ViSOL));
  }

  /**
   * If IPLEX Mode is selected and loggedInUsers is 0,
   *  a list of newly created logged-in users is returned.
   * @returns
   */
  private async setIPLEXModeUser(): Promise<Array<LoggedInUserSchema>> {
    const result: Subject<Array<LoggedInUserSchema>> = new Subject<Array<LoggedInUserSchema>>();

    this.standaloneDataService.createIPLEXModeUser()
      .pipe(
        mergeMap(() => this.loggedInUserDbService.getAll()),
      )
      .subscribe(response => {
        result.next(response);
        result.complete();
      });

    return await firstValueFrom(result);
  }

}
